import type { MealPick, WineListRow } from "@corklog/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { ApiError, suggestForMeal } from "../api.js";
import { actionButton, control, fieldLabel, narrowed, resting } from "./controls.js";
import { AnswerGrounds } from "./AnswerGrounds.js";
import { StatusLine } from "./StatusLine.js";
import { WineRow } from "./WineRow.js";
import { AiNotice } from "./AiNotice.js";

/**
 * Task `10.1` — *I am cooking this; which bottle should I open?* The asking
 * and the answer; the heading, the intro and the empty cellar belong to the
 * page around it, `screens/Meal.tsx`.
 *
 * **In a section of its own since 2026-09-18**, which is the reverse of what
 * `project-scope.md` decided the same day. It was built onto *Att öppna* on
 * the argument that the meal is one more filter on a question that page
 * already asks; what that overlooked is that both lists are drawn with
 * `WineRow`, so a wine recommended for tonight's dinner and a wine whose
 * window is closing sat in identical rows under one heading. The reversal is
 * in the decision, and `screens/Meal.tsx` carries the argument.
 *
 * **The answer names wines by id, and every row is drawn from the list the
 * page already holds.** So the producer, the vintage, the band and — the half
 * that makes the advice actionable before dinner — *which cabinet and which
 * shelf* all come from the cellar's own record. A wine the model invented
 * cannot appear, and the shelf cannot be got wrong, because neither was ever
 * the model's to say.
 */
type State =
  | { status: "idle" }
  | { status: "asking" }
  | {
      status: "answered";
      picks: MealPick[];
      note: string | null;
      dish: string;
      offered: number;
      withheld: number;
    }
  | { status: "failed"; code: string };

export function MealSuggestion({
  cellarId,
  wines,
  currentYear,
}: {
  cellarId: string;
  /** The list this page already fetched. Rows are looked up by id, so a pick
   *  the list does not know about is simply not drawn. */
  wines: readonly WineListRow[];
  currentYear: number;
}) {
  const { t } = useTranslation();
  const [dish, setDish] = useState("");
  const [state, setState] = useState<State>({ status: "idle" });

  const ask = (event: React.FormEvent) => {
    event.preventDefault();
    const asked = dish.trim();
    if (asked.length < 2) return;
    setState({ status: "asking" });
    void suggestForMeal(cellarId, asked)
      .then((answer) =>
        setState({
          status: "answered",
          picks: answer.picks,
          note: answer.note,
          dish: asked,
          offered: answer.offered,
          withheld: answer.withheld,
        }),
      )
      .catch((error: unknown) =>
        setState({
          status: "failed",
          code: error instanceof ApiError ? error.code : "unknown",
        }),
      );
  };

  const byId = new Map(wines.map((wine) => [wine.id, wine]));
  // What the model was told about each wine it picked, in the order picked.
  const told = state.status === "answered" ? state.picks.map((pick) => pick.told) : [];

  return (
    // No rule above it and no heading of its own any more: this is the whole
    // of its page, and a border separating it from nothing is a seam drawn for
    // its own sake.
    <div aria-busy={state.status === "asking"}>
      {/* The shared controls rather than this component's own, which is what
          driving the resting state caught: a hand-rolled field is the wrong
          height for a thumb in front of a cabinet, and a hand-rolled button is
          grey where everything in this interface that acts is brass. The field
          turns brass once it holds something, on the rule the filters follow —
          the mark means *this is doing something*. */}
      <form onSubmit={ask} className="mt-6 flex max-w-prose flex-wrap items-end gap-3">
        <label className="min-w-48 flex-1">
          <span className={fieldLabel}>{t("meal.label")}</span>
          <input
            type="text"
            value={dish}
            maxLength={200}
            onChange={(event) => setDish(event.target.value)}
            placeholder={t("meal.placeholder")}
            className={`${control} ${dish.trim() === "" ? resting : narrowed} mt-1`}
          />
        </label>
        <button
          type="submit"
          disabled={state.status === "asking" || dish.trim().length < 2}
          className={actionButton}
        >
          {state.status === "asking" ? t("meal.asking") : t("meal.ask")}
        </button>
      </form>

      {/* The sentence that says how long, in the place the answer will appear.
          This is what a skeleton is usually reached for, without promising a
          shape the answer may not keep: the model returns between nothing and
          five wines, so five grey rows would be a promise made by the loading
          state and broken by the result. */}
      {state.status === "asking" && <StatusLine>{t("meal.askingLong")}</StatusLine>}

      {state.status === "failed" && (
        <p role="alert" className="text-past mt-4 max-w-prose text-sm">
          {/* Three refusals worth their own sentence, because each has a
              different thing to do about it: no key is the feature switched
              off, nothing openable is a fact about the cellar rather than a
              fault, and the rest is a failed call worth retrying. A fourth
              since 2026-09-28: the administrator's switch, thrown while this
              page was open. */}
          {t(
            state.code === "not_configured"
              ? "meal.notConfigured"
              : state.code === "switched_off"
                ? "features.switchedOff"
                : state.code === "nothing_to_open"
                  ? "meal.nothingToOpen"
                  : "meal.failed",
          )}
        </p>
      )}

      {state.status === "answered" && (
        <div>
          {/* The same region the waiting sentence used, so the arrival is a
              change to it rather than a new element appearing in silence. It
              carries the count, which is the one thing somebody listening
              cannot get from the rows below. */}
          <StatusLine>
            {t("meal.answered", { count: state.picks.length, dish: state.dish })}
          </StatusLine>
          <AiNotice className="mt-2" />
          {state.note !== null && state.note !== "" && (
            <p className="text-muted mt-2 max-w-prose text-sm">{state.note}</p>
          )}

          {/* `WineRow` is the `<li>`, so the reason goes *into* it rather than
              beside it. Wrapping it in a list item of this component's own, to
              have somewhere to put the sentence, nested `<li>` inside `<li>` —
              invalid, and caught by CI rather than by the tests, which passed
              while React warned about it. The shape is `ToOpen`'s. */}
          {state.picks.length === 0 ? (
            <p className="text-muted mt-4 max-w-prose text-sm">{t("meal.noPicks")}</p>
          ) : (
            <ul className="border-line mt-4 border-t">
              {state.picks.flatMap((pick) => {
                const wine = byId.get(pick.wineId);
                if (wine === undefined) return [];
                return [
                  <Link
                    key={pick.wineId}
                    to={`/kallare/${cellarId}/viner/${wine.id}`}
                    className="block"
                    aria-label={wine.name}
                  >
                    <WineRow
                      currentYear={currentYear}
                      note={pick.reason}
                      wine={{
                        number: wine.number,
                        name: wine.name,
                        producer: wine.producer,
                        region: wine.region,
                        countryCode: wine.countryCode,
                        vintage: wine.isNonVintage ? null : wine.vintage,
                        window: {
                          drinkFrom: wine.drinkFrom,
                          peakFrom: wine.peakFrom,
                          peakTo: wine.peakTo,
                          drinkBy: wine.drinkBy,
                        },
                        standing: wine.standing,
                        away: wine.away,
                        ratings: wine.ratings,
                      }}
                    />
                  </Link>,
                ];
              })}
            </ul>
          )}

          {/* What the answer rests on. Once for the whole answer rather than
              per row, because every row is a link and a fold inside a link is
              a click that navigates. How wide the choice was comes first: *five
              picked from eighty-four* and *from three* are different answers. */}
          <p className="text-muted mt-4 max-w-prose text-xs">
            {t("meal.offered", { count: state.offered })}
            {state.withheld > 0 && ` ${t("meal.withheld", { count: state.withheld })}`}
          </p>
          {told.length > 0 && <AnswerGrounds labels={[]} told={told.join("\n\n")} />}

          <p className="text-muted mt-4 max-w-prose text-xs">{t("meal.disclaimer")}</p>
        </div>
      )}
    </div>
  );
}
